import { ResourceAccessState } from "@eai-toolkit/client";
import { tracker } from "@openreplay/tracker";
import {
  dehydrateAccessRequestsQueryState,
  dehydrateAccessRequestsQueryStateToString,
  hydrateAccessRequestsQueryState,
} from "@store/queryState";
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  DEFAULT_CHIPS,
  type FilterName,
  LoadOverlay,
  QuickFilters,
  ViewWrapper,
} from "@components";
import {
  NameIdSearch,
  type SearchFilter,
} from "@components/NameIdSearch/NameIdSearch";
import { StateSelector } from "@components/StateSelector/StateSelector";
import { UserRoleSelector } from "@components/UserRoleSelector/UserRoleSelector";
import {
  useValidationContextValue,
  ValidationContext,
} from "@components/ValidationContext/ValidationContext";
import {
  ActionButton,
  FilterBar,
  NoneFoundMessage,
  SearchRow,
} from "@components/ViewWrapper/styles";

import {
  ACCESS_REQUEST_DETAIL_SEGMENT,
  NEW_ACCESS_REQUEST_ROUTE,
} from "@helpers/constants";
import { ACCESS_REQUEST_EVENTS } from "@helpers/events";

import { useDataTableUrlInit } from "../../../components/DataTable";
import DataTable from "../../../components/DataTable/DataTable";
import { useRowNavigation } from "../../../hooks/useRowNavigation";
import { useGetAccessRequestsQuery } from "../../../store/api/accessRequestsApi";
import {
  useGetAccessRequestRequestorsQuery,
  useGetMeQuery,
} from "../../../store/api/usersApi";
import { useFilters } from "../../../store/hooks/useFilters";
import { useQueryStateFromUrl } from "../../../store/hooks/useQueryStateFromUrl";
import { useTableControls } from "../../../store/hooks/useTableControls";
import { useUpdateQueryState } from "../../../store/hooks/useUpdateQueryState";
import fieldRenderers from "./data/fieldRenderers";
import {
  defaultFieldLabels,
  defaultSortField,
  fields,
  getDefaultSortOrder,
} from "./data/fields";
import type { LocalAccessRequest } from "./data/types";
import { getStateBackgroundColor } from "./utils";

// Order determines display order in the state filter dropdown.
// Compile-time check: will error if a ResourceAccessState value is added/removed from the API.
const ACCESS_REQUEST_STATES = [
  ResourceAccessState.PENDING,
  ResourceAccessState.APPROVED,
  ResourceAccessState.REJECTED,
  ResourceAccessState.CANCELLED,
  ResourceAccessState.EXPIRED,
] as const;
{
  // eslint-disable-next-line
  const _exhaustiveCheck: ResourceAccessState =
    "" as (typeof ACCESS_REQUEST_STATES)[number];
  // eslint-disable-next-line
  const _reverseCheck: (typeof ACCESS_REQUEST_STATES)[number] =
    "" as ResourceAccessState;
}

const QUICK_FILTERS = [
  { ...DEFAULT_CHIPS.isMine, label: "Requested by me" },
  DEFAULT_CHIPS.isRequestsToMe,
  DEFAULT_CHIPS.isPendingMyApproval,
  DEFAULT_CHIPS.isToday,
  DEFAULT_CHIPS.isWeek,
];

const STATE_SELECTOR_OPTIONS = ACCESS_REQUEST_STATES;

const COLUMNS = [
  { ...fields.requestId, width: 118, visibleByDefault: true },
  { ...fields.resource, visibleByDefault: true },
  { ...fields.scope },
  { ...fields.action, visibleByDefault: true },
  { ...fields.target, visibleByDefault: true },
  { ...fields.requestedBy, visibleByDefault: true },
  { ...fields.processedBy },
  { ...fields.state, width: 130, visibleByDefault: true },
  { ...fields.expiresAt, visibleByDefault: true },
  { ...fields.expiration },
  { ...fields.createdAt },
  { ...fields.recipients },
  { ...fields.requestReason },
].map((column) => ({
  ...column,
  label: defaultFieldLabels[column.fieldName],
  defaultSortOrder: getDefaultSortOrder(column),
}));

const AccessRequestsTable = () => {
  const handleRowClick = useRowNavigation<LocalAccessRequest>(
    (row) => `${ACCESS_REQUEST_DETAIL_SEGMENT}/${row.id}`,
  );

  const { data: me } = useGetMeQuery();
  const { data: requestorsList } = useGetAccessRequestRequestorsQuery();
  // Pass requestors as-is — UserRoleSelector adds the current user to the map
  // internally and renders them under the YOU group independently.
  const requestors = requestorsList;

  const queryState = useQueryStateFromUrl(hydrateAccessRequestsQueryState);
  const updateQueryState = useUpdateQueryState(
    dehydrateAccessRequestsQueryState,
  );

  const queryKey = useMemo(
    () => dehydrateAccessRequestsQueryStateToString(queryState),
    [queryState],
  );

  const {
    data: response,
    isLoading,
    error,
  } = useGetAccessRequestsQuery({ queryKey }, { pollingInterval: 30000 });

  const { handleSortChange, handlePrevPage, handleNextPage } =
    useTableControls<LocalAccessRequest>({
      queryState,
      updateQueryState,
    });

  useDataTableUrlInit({
    tableId: "access-requests-table",
    enableColumnSelection: true,
    allColumns: COLUMNS,
  });

  const [columnsButton, setColumnsButton] = useState<React.ReactNode>(null);
  const handleColumnsButtonRender = useCallback((button: React.ReactNode) => {
    setColumnsButton(button);
  }, []);

  const { handleCreatorChange, onSetQuickFilter, quickFilterState } =
    useFilters<LocalAccessRequest>({
      queryState,
      updateQueryState,
      currentUserId: me?.id,
      fieldNames: {
        creator: "requestedBy",
        date: "createdAt",
        recipients: "recipients",
      },
    });

  // "Awaiting my approval" is active when the user is a recipient AND state is exactly PENDING.
  // This is mutually exclusive with "Requests to me" (recipients=me, any state).
  const isPendingMyApprovalActive = useMemo(() => {
    if (!me?.id) return false;
    const recipients =
      queryState.query.filters.get("recipients")?.valueList ?? [];
    const states = queryState.query.filters.get("state")?.valueList ?? [];
    return (
      recipients.length === 1 &&
      recipients[0] === me.id &&
      states.length === 1 &&
      states[0] === ResourceAccessState.PENDING
    );
  }, [queryState.query.filters, me?.id]);

  // When "Awaiting my approval" is active, suppress "Requests to me" so only one shows as selected.
  const mergedQuickFilterState = useMemo(
    () => ({
      ...quickFilterState,
      isRequestsToMe:
        quickFilterState.isRequestsToMe && !isPendingMyApprovalActive,
      isPendingMyApproval: isPendingMyApprovalActive,
    }),
    [quickFilterState, isPendingMyApprovalActive],
  );

  const handleSetQuickFilter = useCallback(
    (filter: { filterName: FilterName; isSelected: boolean }) => {
      tracker.event(
        ACCESS_REQUEST_EVENTS.QUICK_FILTER_APPLIED,
        filter.filterName,
      );
      const { filterName, isSelected } = filter;
      const emptyFilter = {
        booleanValueList: [] as boolean[],
        fuzzyMatch: null,
        range: [] as string[],
      };

      if (filterName === "isPendingMyApproval") {
        const newFilters = new Map(queryState.query.filters);
        if (isSelected && me?.id) {
          newFilters.set("recipients", { valueList: [me.id], ...emptyFilter });
          newFilters.set("state", {
            valueList: [ResourceAccessState.PENDING],
            ...emptyFilter,
          });
          newFilters.delete("requestedBy");
        } else {
          newFilters.delete("recipients");
          newFilters.delete("state");
        }
        updateQueryState({
          ...queryState,
          query: { ...queryState.query, filters: newFilters },
          paging: { ...queryState.paging, offset: 0 },
        });
        return;
      }

      // When switching to "Requested by me" or "Requests to me" while "Awaiting my approval"
      // is active, batch the state-filter clear with the filter change.
      if (
        isSelected &&
        isPendingMyApprovalActive &&
        (filterName === "isMine" || filterName === "isRequestsToMe")
      ) {
        const newFilters = new Map(queryState.query.filters);
        newFilters.delete("state");
        if (filterName === "isMine" && me?.id) {
          newFilters.set("requestedBy", { valueList: [me.id], ...emptyFilter });
          newFilters.delete("recipients");
        } else if (filterName === "isRequestsToMe" && me?.id) {
          newFilters.set("recipients", { valueList: [me.id], ...emptyFilter });
          newFilters.delete("requestedBy");
        }
        updateQueryState({
          ...queryState,
          query: { ...queryState.query, filters: newFilters },
          paging: { ...queryState.paging, offset: 0 },
        });
        return;
      }

      onSetQuickFilter({ filter });
    },
    [
      me?.id,
      queryState,
      updateQueryState,
      onSetQuickFilter,
      isPendingMyApprovalActive,
    ],
  );

  const { validationContextValue } = useValidationContextValue();

  const statesFilterValue = (queryState.query.filters.get("state")?.valueList ??
    []) as ResourceAccessState[];

  const requestedByFilterValue =
    queryState.query.filters.get("requestedBy")?.valueList[0] ?? null;

  const idFilterValue =
    queryState.query.filters.get("requestId")?.valueList[0] ?? "";

  const handleStatesChange = useCallback(
    (states: ResourceAccessState[]) => {
      const newFilters = new Map(queryState.query.filters);
      if (states.length > 0) {
        newFilters.set("state", {
          valueList: states,
          booleanValueList: [],
          fuzzyMatch: null,
          range: [],
        });
      } else {
        newFilters.delete("state");
      }
      updateQueryState({
        ...queryState,
        query: { ...queryState.query, filters: newFilters },
        paging: { ...queryState.paging, offset: 0 },
      });
      tracker.event(
        ACCESS_REQUEST_EVENTS.STATE_FILTER_CHANGED,
        states.join(",") || "cleared",
      );
    },
    [queryState, updateQueryState],
  );

  const handleSearchChange = useCallback(
    (filter: SearchFilter) => {
      const newFilters = new Map(queryState.query.filters);
      if (filter.value) {
        newFilters.set("requestId", {
          valueList: [filter.value],
          booleanValueList: [],
          fuzzyMatch: null,
          range: [],
        });
      } else {
        newFilters.delete("requestId");
      }
      updateQueryState({
        ...queryState,
        query: { ...queryState.query, filters: newFilters },
        paging: { ...queryState.paging, offset: 0 },
      });
    },
    [queryState, updateQueryState],
  );

  const data = response?.items ?? [];
  const totalItems = response?.metadata?.total ?? 0;

  const paging = useMemo(
    () => ({
      ...queryState.paging,
      total: {
        count: totalItems,
        isCountFinal: true,
      },
    }),
    [queryState.paging, totalItems],
  );

  return (
    <ViewWrapper
      docsSection="ACCESS_REQUESTS"
      title="Access Requests"
      aboveContent={
        <>
          <ValidationContext.Provider value={validationContextValue}>
            <FilterBar>
              <UserRoleSelector
                value={requestedByFilterValue}
                onChange={handleCreatorChange}
                usersOnly
                label="Requested By"
                overrideUsers={requestors}
              />
              <StateSelector
                value={statesFilterValue}
                onChange={handleStatesChange}
                options={STATE_SELECTOR_OPTIONS}
                getStateColor={getStateBackgroundColor}
              />
            </FilterBar>
          </ValidationContext.Provider>
        </>
      }
    >
      <SearchRow>
        <NameIdSearch
          value={idFilterValue}
          onChange={handleSearchChange}
          idOnly
        />
        <ActionButton
          color="primary"
          variant="contained"
          component={Link}
          to={NEW_ACCESS_REQUEST_ROUTE}
          state={{ canGoBack: true }}
          onClick={() =>
            tracker.event(ACCESS_REQUEST_EVENTS.NEW_REQUEST_BUTTON_CLICKED)
          }
        >
          New Request
        </ActionButton>
      </SearchRow>
      <QuickFilters
        filters={QUICK_FILTERS}
        filterState={mergedQuickFilterState}
        setQuickFilter={handleSetQuickFilter}
        columnsButton={columnsButton}
      />
      <LoadOverlay loading={isLoading} />
      {error && (
        <p role="alert" style={{ color: "red" }}>
          Error: Failed to load access requests
        </p>
      )}
      {!isLoading && !error && data.length === 0 && (
        <NoneFoundMessage>
          No access requests found for this query. Try adjusting your filters.
        </NoneFoundMessage>
      )}
      {!error && data.length > 0 && (
        <DataTable
          ariaLabel="Access requests table"
          columns={COLUMNS}
          data={data}
          fieldRenderers={fieldRenderers}
          sort={queryState.query.sort}
          paging={paging}
          setSort={handleSortChange}
          onPrevPageClick={handlePrevPage}
          onNextPageClick={handleNextPage}
          onRowClick={handleRowClick}
          actions={[]}
          enableColumnSelection={true}
          renderColumnsButtonExternally={true}
          onRenderColumnsButton={handleColumnsButtonRender}
          tableId="access-requests-table"
          defaultSort={{
            fieldName: defaultSortField.fieldName,
            direction: getDefaultSortOrder(defaultSortField),
          }}
        />
      )}
    </ViewWrapper>
  );
};

export default AccessRequestsTable;
